"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, Copy, Eye, ImageIcon, Sparkles, Wrench } from "lucide-react";
import { useState } from "react";
import { ModelDiscountBadge } from "@/components/model-discount-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useApi } from "@/lib/fetch-client";
import { formatPrice } from "@/lib/model-utils";
import { effectiveUnitPrice, isMappingDeactivated } from "@llmgateway/shared/components";
function CatalogPricing({
  modelId,
  image = false
}: {
  modelId: string;
  image?: boolean;
}) {
  const api = useApi();
  const {
    data,
    isPending,
    isError
  } = api.useQuery("get", "/internal/models", {}, {
    staleTime: 60_000
  });
  const model = data?.models.find(candidate => candidate.id === modelId);
  const mappings = model?.mappings.filter(mapping => mapping.status === "active" && !isMappingDeactivated(mapping)) ?? [];
  if (isPending || isError || mappings.length === 0) {
    return <p className="text-xs text-muted-foreground">
				{isPending ? "Loading prices…" : "Pricing unavailable"}
			</p>;
  }
  const fields = image ? ["requestPrice"] as const : ["inputPrice", "outputPrice"] as const;
  return <div className="text-xs space-y-1">
			<div className="text-muted-foreground">Pricing from:</div>
			<div className="flex gap-3">
				{fields.map(field => {
        const prices = mappings.map(mapping_0 => effectiveUnitPrice(mapping_0[field], mapping_0.discount)).filter((price): price is number => price !== null);
        const price_0 = prices.length ? Math.min(...prices) : null;
        return <span key={field}>
							<span className="font-mono font-medium">
								{price_0 === null ? "—" : image ? `$${price_0.toFixed(3)}/req` : formatPrice(price_0)}
							</span>{" "}
							{!image && <span className="text-muted-foreground">
									{field === "inputPrice" ? "in" : "out"}
								</span>}
						</span>;
      })}
			</div>
			<ModelDiscountBadge mappings={mappings} />
		</div>;
}

/**
 * Model data structure from list-models tool
 */
interface ModelData {
  id: string;
  name: string;
  family?: string;
  providers: string[];
  capabilities: {
    vision: boolean;
    tools: boolean;
    reasoning: boolean;
    streaming: boolean;
    imageGeneration: boolean;
  };
  pricing: {
    input: string;
    output: string;
  };
  context_length?: number;
  free: boolean;
}

/**
 * Image model data structure from list-image-models tool
 */
interface ImageModelData {
  id: string;
  name: string;
  description?: string;
  family?: string;
  requestPrice?: number;
  providers: string[];
}

/**
 * Type guard to check if output contains models list
 */
export function hasModelsOutput(output: unknown): output is {
  text: string;
  models: ModelData[];
} {
  if (typeof output !== "object" || output === null) {
    return false;
  }
  if (!("models" in output) || !Array.isArray((output as any).models)) {
    return false;
  }
  const models = (output as any).models;
  if (models.length === 0) {
    return false;
  }
  const first = models[0];
  return typeof first === "object" && first !== null && "id" in first && "capabilities" in first;
}

/**
 * Type guard to check if output contains image models list
 */
export function hasImageModelsOutput(output: unknown): output is {
  text: string;
  imageModels: ImageModelData[];
} {
  if (typeof output !== "object" || output === null) {
    return false;
  }
  if (!("imageModels" in output) || !Array.isArray((output as any).imageModels)) {
    return false;
  }
  const models = (output as any).imageModels;
  if (models.length === 0) {
    return false;
  }
  const first = models[0];
  return typeof first === "object" && first !== null && "id" in first;
}

/**
 * Model card component for displaying LLM models
 */
function ModelCard(t0) {
  const $ = _c(50);
  const {
    model
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== model.id) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(model.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = model.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyToClipboard = t1;
  let capabilities;
  if ($[2] !== model.capabilities.imageGeneration || $[3] !== model.capabilities.reasoning || $[4] !== model.capabilities.tools || $[5] !== model.capabilities.vision) {
    capabilities = [];
    if (model.capabilities.vision) {
      capabilities.push("Vision");
    }
    if (model.capabilities.tools) {
      capabilities.push("Tools");
    }
    if (model.capabilities.reasoning) {
      capabilities.push("Reasoning");
    }
    if (model.capabilities.imageGeneration) {
      capabilities.push("Image Gen");
    }
    $[2] = model.capabilities.imageGeneration;
    $[3] = model.capabilities.reasoning;
    $[4] = model.capabilities.tools;
    $[5] = model.capabilities.vision;
    $[6] = capabilities;
  } else {
    capabilities = $[6];
  }
  const formatContextSize = _temp;
  let t2;
  if ($[7] !== model.name) {
    t2 = <h4 className="font-semibold text-sm truncate">{model.name}</h4>;
    $[7] = model.name;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== model.family) {
    t3 = model.family && <Badge variant="secondary" className="mt-1 text-[10px]">{model.family}</Badge>;
    $[9] = model.family;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== model.free) {
    t5 = model.free && <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px]">FREE</Badge>;
    $[14] = model.free;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <div className="flex items-start justify-between gap-2">{t4}{t5}</div>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== model.id) {
    t7 = <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code>;
    $[19] = model.id;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== copied) {
    t8 = copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />;
    $[21] = copied;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== copyToClipboard || $[24] !== t8) {
    t9 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={copyToClipboard} title="Copy model ID">{t8}</Button>;
    $[23] = copyToClipboard;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t7 || $[27] !== t9) {
    t10 = <div className="flex items-center gap-2">{t7}{t9}</div>;
    $[26] = t7;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-muted-foreground">Context:</span>;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== model.context_length) {
    t12 = formatContextSize(model.context_length) ?? "\u2014";
    $[30] = model.context_length;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12) {
    t13 = <div>{t11}<span className="ml-1 font-medium">{t12}</span></div>;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-muted-foreground">Providers:</span>;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== model.providers.length) {
    t15 = <div>{t14}<span className="ml-1 font-medium">{model.providers.length}</span></div>;
    $[35] = model.providers.length;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t13 || $[38] !== t15) {
    t16 = <div className="grid grid-cols-2 gap-2 text-xs">{t13}{t15}</div>;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== model.id) {
    t17 = <CatalogPricing modelId={model.id} />;
    $[40] = model.id;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== capabilities) {
    t18 = capabilities.length > 0 && <div className="flex flex-wrap gap-1">{capabilities.map(_temp2)}</div>;
    $[42] = capabilities;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t10 || $[45] !== t16 || $[46] !== t17 || $[47] !== t18 || $[48] !== t6) {
    t19 = <div className="flex-shrink-0 w-[280px] rounded-lg border bg-card p-4 space-y-3">{t6}{t10}{t16}{t17}{t18}</div>;
    $[44] = t10;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t6;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
}

/**
 * Image model card component
 */
function _temp2(cap) {
  return <Badge key={cap} variant="outline" className="text-[10px] px-1.5 py-0">{cap === "Vision" && <Eye className="h-2.5 w-2.5 mr-0.5" />}{cap === "Tools" && <Wrench className="h-2.5 w-2.5 mr-0.5" />}{cap === "Reasoning" && <Sparkles className="h-2.5 w-2.5 mr-0.5" />}{cap === "Image Gen" && <ImageIcon className="h-2.5 w-2.5 mr-0.5" />}{cap}</Badge>;
}
function _temp(size) {
  if (!size) {
    return null;
  }
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}K`;
  }
  return size.toString();
}
function ImageModelCard(t0) {
  const $ = _c(31);
  const {
    model
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== model.id) {
    t1 = async () => {
      try {
        await navigator.clipboard.writeText(model.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    };
    $[0] = model.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyToClipboard = t1;
  let t2;
  if ($[2] !== model.name) {
    t2 = <h4 className="font-semibold text-sm truncate">{model.name}</h4>;
    $[2] = model.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== model.family) {
    t3 = model.family && <Badge variant="secondary" className="mt-1 text-[10px]">{model.family}</Badge>;
    $[4] = model.family;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex-1 min-w-0">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ImageIcon className="h-4 w-4 text-muted-foreground" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4) {
    t6 = <div className="flex items-start justify-between gap-2">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== model.description) {
    t7 = model.description && <p className="text-xs text-muted-foreground line-clamp-2">{model.description}</p>;
    $[12] = model.description;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== model.id) {
    t8 = <code className="flex-1 text-xs bg-muted px-2 py-1 rounded font-mono truncate">{model.id}</code>;
    $[14] = model.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== copied) {
    t9 = copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />;
    $[16] = copied;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== copyToClipboard || $[19] !== t9) {
    t10 = <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" onClick={copyToClipboard} title="Copy model ID">{t9}</Button>;
    $[18] = copyToClipboard;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <div className="flex items-center gap-2">{t8}{t10}</div>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== model.id) {
    t12 = <CatalogPricing modelId={model.id} image={true} />;
    $[24] = model.id;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12 || $[28] !== t6 || $[29] !== t7) {
    t13 = <div className="flex-shrink-0 w-[260px] rounded-lg border bg-card p-4 space-y-3">{t6}{t7}{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t6;
    $[29] = t7;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}

/**
 * Models list output component with horizontal scroll
 */
export function ModelsListOutput(t0) {
  const $ = _c(13);
  const {
    models
  } = t0;
  const t1 = models.length !== 1 ? "s" : "";
  let t2;
  if ($[0] !== models.length || $[1] !== t1) {
    t2 = <div className="flex items-center justify-between px-2"><span className="text-xs text-muted-foreground">{models.length} model{t1} found</span></div>;
    $[0] = models.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== models) {
    t3 = models.map(_temp3);
    $[3] = models;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="flex gap-3 p-2">{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ScrollBar orientation="horizontal" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <ScrollArea className="w-full whitespace-nowrap">{t4}{t5}</ScrollArea>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <div className="space-y-2 py-2">{t2}{t6}</div>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * Image models list output component with horizontal scroll
 */
function _temp3(model) {
  return <ModelCard key={model.id} model={model} />;
}
export function ImageModelsListOutput(t0) {
  const $ = _c(13);
  const {
    imageModels
  } = t0;
  const t1 = imageModels.length !== 1 ? "s" : "";
  let t2;
  if ($[0] !== imageModels.length || $[1] !== t1) {
    t2 = <div className="flex items-center justify-between px-2"><span className="text-xs text-muted-foreground">{imageModels.length} image model{t1}{" "}found</span></div>;
    $[0] = imageModels.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== imageModels) {
    t3 = imageModels.map(_temp4);
    $[3] = imageModels;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="flex gap-3 p-2">{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ScrollBar orientation="horizontal" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <ScrollArea className="w-full whitespace-nowrap">{t4}{t5}</ScrollArea>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <div className="space-y-2 py-2">{t2}{t6}</div>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}

/**
 * Text content output component for generate_content results
 */
function _temp4(model) {
  return <ImageModelCard key={model.id} model={model} />;
}
export function TextContentOutput(t0) {
  const $ = _c(2);
  const {
    response
  } = t0;
  let t1;
  if ($[0] !== response) {
    t1 = <div className="p-3 rounded-md bg-muted/30 border"><div className="prose prose-sm dark:prose-invert max-w-none"><div className="whitespace-pre-wrap text-sm">{response}</div></div></div>;
    $[0] = response;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Type guard for generate_content output
 */
export function hasTextContentOutput(output: unknown): output is {
  response: string;
} {
  return typeof output === "object" && output !== null && "response" in output && typeof (output as any).response === "string";
}